import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Card, Container, Nav, Tab } from 'react-bootstrap';
// import { FiCheck } from 'react-icons/fi';
import SessionsServices from '../../services/sessions'
import { SweetAlert } from '../../components/sweetalert';
import { useNavigate, useParams } from 'react-router-dom';
import { URL } from '../../config/url';
import Usuarios from '../../services/Usuarios';
import { useAuth } from '../../context/auth';
import TabUsuario from './TabUsuario';
import { IoMdArrowRoundBack } from 'react-icons/io';
import TabPermissions from './TabPermissions';
import Loading from '../../routers/loading';
import { canSeeFinancialTotal, getRetroactiveDays } from '../../utilities/financialPeriod';
import { DashboardPermissions } from './TabPermissions';
import { DASHBOARD_TABS, getDashboardTabs } from '../../utilities/dashboard';

function CriarUsuarioInterno() {
  const params = useParams()

  const [loadingRecuperar, setLoadingRecuperar] = useState(false)
  const [, setEmpresas] = useState([])

  const { register, getValues, formState: { errors }, setValue, watch } = useForm();
  const { user: loggedUser } = useAuth();

  // Aba Permissões: só na edição de outro usuário, para quem tem a feature e não tem restrição no Financeiro
  // (período ou valor total oculto), mesmas regras do backend
  const canConfigurePeriod = Boolean(params.id)
    && (params.id !== loggedUser?.id)
    && Boolean(loggedUser?.features?.includes('update:user:financial_period'))
    && (getRetroactiveDays(loggedUser) === null)
    && canSeeFinancialTotal(loggedUser)

  // Bloco Dashboard da aba Permissões: só na edição de outro usuário, para quem tem a feature e ao menos uma aba (mesmas regras do backend)
  const loggedDashboardTabs = getDashboardTabs(loggedUser).map((tab) => tab.key)
  const canConfigureDashboard = Boolean(params.id)
    && (params.id !== loggedUser?.id)
    && Boolean(loggedUser?.features?.includes('update:user:dashboard'))
    && (loggedDashboardTabs.length > 0)
  const [dashboardTarget, setDashboardTarget] = useState({ tabs: [], admin: false })
  // Espelho da regra do backend: o administrador não é alterado, e quem tem aba que você não tem fica travado
  const dashboardEditable = !dashboardTarget.admin && dashboardTarget.tabs.every((tab) => loggedDashboardTabs.includes(tab))

  useEffect(() => {
    if (params.id) {
      setLoadingRecuperar(true)
      Usuarios.listId(params.id)
        .then((data) => {
          setEmpresas(data.data.empresas_usuarios)
          setValue('email', data.data.email);
          setValue('nome', data.data.name);
          setValue('username', data.data.username);
          setValue('password', '');
          setValue('status', data.data.activated);

          // Período do Financeiro. Valores em texto no Select, para não repetir o problema do Status.
          const days = data.data.parameters?.financial_retroactive_days ?? null
          setValue('financial_restricted', days === null ? 'false' : 'true');
          setValue('financial_days', days === null ? '' : String(days));
          setValue('financial_show_total', canSeeFinancialTotal(data.data) ? 'true' : 'false');

          // Abas do Dashboard (o backend já devolve todas para o administrador)
          const dashboardTabs = data.data.parameters?.dashboard_tabs ?? []
          setDashboardTarget({ tabs: dashboardTabs, admin: Boolean(data.data.features?.includes('update:user:feature')) })
          DASHBOARD_TABS.forEach((tab) => setValue('dashboard_tab_' + tab.key, dashboardTabs.includes(tab.key)))
        })
        .catch((error) => {
          SweetAlert.error(error.response.data.message, 'Conta');
        })
        .finally(() => { setLoadingRecuperar(false) })

    }
  }, []);

  const navigate = useNavigate()

  async function criarUsuario() {
    const user = {
      "email": getValues('email'),
      "name": getValues('nome'),
      "username": getValues('username'),
      "password": getValues('password'),
      // String(): na carga o setValue grava boolean; o Select só devolve texto quando é alterado
      "activated": String(getValues('status')) === 'true' ? true : false,
    }

    if (params.id) {
      try {
        setLoadingRecuperar(true)
        await SessionsServices.updateUser(user, params.id)
        SweetAlert.success('Usuário atualizado com sucesso!', 'Usuário')
        navigate(URL.usuarios_lista)
        setLoadingRecuperar(false)
      } catch (error) {
        setLoadingRecuperar(false)
        SweetAlert.error(error.response.data.message, 'Usuário');
      }

    } else {
      try {
        setLoadingRecuperar(true)
        await SessionsServices.crateUser(user)
        SweetAlert.success('Usuário criado com sucesso! Verifique seu email e ative sua conta', 'Usuário')
        navigate(URL.login)
        setLoadingRecuperar(false)
      } catch (error) {
        setLoadingRecuperar(false)
        SweetAlert.error(error.response.data.message, 'Usuário');
      }
    }
  }

  async function saveFinancialPeriod() {
    const restricted = getValues('financial_restricted') === 'true'
    const days = getValues('financial_days')

    if (restricted && ((days === '') || (days === undefined)))
      return SweetAlert.error('Informe quantos dias anteriores o usuário pode ver (0 = somente hoje).', 'Usuário')

    if (restricted && (Number(days) > 365))
      return SweetAlert.error('O máximo é 365 dias.', 'Usuário')

    try {
      setLoadingRecuperar(true)
      await Usuarios.updateFinancialPeriod(params.id, {
        financial_retroactive_days: restricted ? Number(days) : null,
        financial_show_total: getValues('financial_show_total') !== 'false',
      })
      SweetAlert.success('Permissões do Financeiro atualizadas com sucesso!', 'Usuário')
      setLoadingRecuperar(false)
    } catch (error) {
      setLoadingRecuperar(false)
      SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Usuário');
    }
  }

  async function saveDashboardTabs() {
    // As abas que você não tem mantêm o valor atual do usuário editado
    const dashboard_tabs = DASHBOARD_TABS
      .filter((tab) => loggedDashboardTabs.includes(tab.key) ? getValues('dashboard_tab_' + tab.key) === true : dashboardTarget.tabs.includes(tab.key))
      .map((tab) => tab.key)

    try {
      setLoadingRecuperar(true)
      const { data } = await Usuarios.updateDashboardTabs(params.id, { dashboard_tabs })
      setDashboardTarget((current) => ({ ...current, tabs: data?.parameters?.dashboard_tabs ?? dashboard_tabs }))
      SweetAlert.success('Abas do Dashboard atualizadas com sucesso!', 'Usuário')
      setLoadingRecuperar(false)
    } catch (error) {
      setLoadingRecuperar(false)
      SweetAlert.error(error?.response?.data?.message ?? error?.message, 'Usuário');
    }
  }

  return (
    <>
      {loadingRecuperar === true ? <Loading /> : ''}
      <Container className=" pt-2">

        <div className='d-flex justify-content-between align-items-center mb-4'>
          <h2>Usuário</h2>
          <Button variant='outline-secondary' onClick={() => navigate(-1)} className='d-flex align-items-center gap-1'><IoMdArrowRoundBack /> Voltar</Button>
        </div>

        <Card className='col-11 col-md-12 cardShadow d-flex p-3'>
          <Container fluid>


            <Tab.Container id="tabs-example" defaultActiveKey="/home">
              <Nav justify variant="tabs">
                <Nav.Item>
                  <Nav.Link eventKey="/home">Principal</Nav.Link>
                </Nav.Item>
                {/* <Nav.Item>
                  <Nav.Link eventKey="link-1">Empresas</Nav.Link>
                </Nav.Item> */}
                {(canConfigurePeriod || canConfigureDashboard) &&
                  <Nav.Item>
                    <Nav.Link eventKey="link-2">Permissões</Nav.Link>
                  </Nav.Item>}
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="/home" className='p-5'>
                  <TabUsuario
                    params={params}
                    navigate={navigate}
                    register={register}
                    errors={errors}
                    criarUsuario={criarUsuario}
                    loadingRecuperar={loadingRecuperar}
                  />
                </Tab.Pane>
                {/* <Tab.Pane eventKey="link-1">
                  <TabEmpresas
                    empresas={empresas}
                  />
                </Tab.Pane> */}
                {(canConfigurePeriod || canConfigureDashboard) &&
                  <Tab.Pane eventKey="link-2" className='p-5'>
                    {canConfigurePeriod &&
                    <TabPermissions
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      saveFinancialPeriod={saveFinancialPeriod}
                      loadingRecuperar={loadingRecuperar}
                    />}
                    {canConfigureDashboard &&
                      <DashboardPermissions
                        register={register}
                        loggedTabs={loggedDashboardTabs}
                        editable={dashboardEditable}
                        admin={dashboardTarget.admin}
                        separated={canConfigurePeriod}
                        saveDashboardTabs={saveDashboardTabs}
                        loadingRecuperar={loadingRecuperar}
                      />}
                  </Tab.Pane>}
              </Tab.Content>
            </Tab.Container>
          </Container>

        </Card>






      </Container >
    </>
  )
}

export default React.memo(CriarUsuarioInterno)